import { useMemo } from 'react';

import {
    findMarket,
    useTokenPairDetail,
    useTokenPairs,
    useTokenPairStore,
} from '@/features/tokenPairs';

import { getMaintenanceRate } from '../utils/maintenanceRate';

/**
 * Maintenance-margin fraction for the CHARTED market, which is the market both
 * the pre-trade preview and the open-position panel are about.
 *
 * Both queries behind it are the ones the leverage ladder already runs, so this
 * is a cache read rather than extra traffic. `notionalUsd` picks the leverage
 * tier; pass what the position is worth (collateral x leverage before it
 * exists, size x entry after) or null to price it at the base tier.
 */
export function useMaintenanceRate(notionalUsd: number | null): number {
    const networkId = useTokenPairStore((s) => s.networkId);
    const selectedPair = useTokenPairStore((s) => s.selectedPair);
    const selectedCoin = useTokenPairStore((s) => s.selectedCoin);
    const { data: pairDetail } = useTokenPairDetail(networkId, selectedPair);
    const { data: marketsData } = useTokenPairs(networkId);

    return useMemo(
        () =>
            getMaintenanceRate(
                pairDetail?.leverage,
                findMarket(marketsData?.markets, selectedCoin)?.maxLeverage ??
                    null,
                notionalUsd,
            ),
        [pairDetail?.leverage, marketsData, selectedCoin, notionalUsd],
    );
}
